import { useState, useCallback, useRef, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  HardDrive, Upload, FileText, RotateCcw, SlidersHorizontal, Loader2,
  CheckCircle2, XCircle, Clock, AlertTriangle, ChevronDown, ChevronUp,
} from 'lucide-react';
import { apiGet, apiPost } from '@/lib/api';
import { fmtTime, fmtBytes } from '@/lib/format';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import type { Device } from '@/types';

/* ── 後端回傳的命令紀錄 ────────────────────────── */

interface DeviceCommand {
  command_id: string;
  cmd_type: string;
  state: string;
  created_at: string;
  expires_at?: string | null;
  result?: Record<string, unknown> | null;
}

interface DeviceDetailResp {
  commands: DeviceCommand[];
  [k: string]: unknown;
}

/* ── 常量 ────────────────────────────────────────── */

/** 與後端 ALLOWED_COMMANDS 完全一致。value = 需要的能力；null = 不受閘門限制。 */
const ALLOWED_COMMANDS: Record<string, string | null> = {
  get_sd_status: null,
  request_upload: null,
  request_log: null,
  set_threshold: null,
  reboot: 'firmware_ota',
};

/** 命令狀態的中文與配色 */
const CMD_LABEL: Record<string, string> = {
  created: '已建立', sent: '已下發', succeeded: '成功',
  failed: '失敗', timed_out: '逾時',
};
const CMD_VARIANT: Record<string, 'success' | 'warn' | 'danger' | 'muted' | 'info'> = {
  created: 'muted', sent: 'info', succeeded: 'success',
  failed: 'danger', timed_out: 'warn',
};

const CMD_ICON: Record<string, ReactNode> = {
  created: <Clock className="h-3.5 w-3.5" />,
  sent: <Loader2 className="h-3.5 w-3.5 animate-spin" />,
  succeeded: <CheckCircle2 className="h-3.5 w-3.5 text-success" />,
  failed: <XCircle className="h-3.5 w-3.5 text-destructive" />,
  timed_out: <AlertTriangle className="h-3.5 w-3.5" />,
};

/** 命令類型的中文標籤 */
const CMD_TYPE_LABEL: Record<string, string> = {
  get_sd_status: '查詢記憶卡',
  request_upload: '要求上傳',
  request_log: '索取日誌',
  set_threshold: '設定門檻',
  reboot: '重新啟動',
  start_stream: '開始推流',
  stop_stream: '停止推流',
};

/* ── SD 卡狀態呈現 ───────────────────────────────── */

interface SdStatus {
  mounted?: boolean;
  total_bytes?: number;
  used_bytes?: number;
  free_bytes?: number;
  // 韌體回報的 MB 單位欄位（firmware mqtt_client_app.c get_sd_status）
  sd_total_mb?: number;
  sd_free_mb?: number;
  // 模擬器回報的 MB 單位欄位
  total_mb?: number;
  free_mb?: number;
}

/** 解析韌體/模擬器回報的 SD 卡狀態，統一轉成 bytes。
 *
 * 韌體 get_sd_status 回報格式（mqtt_app_publish_command_result）：
 *   {message: "sd_total_mb=32768 sd_free_mb=20000"}
 * 這被 backend handle_command_result 存成 result = {message: "sd_total_mb=..."}
 *
 * 模擬器回報格式：
 *   {message: "get_sd_status 已執行", sd: {total_mb: 32768, free_mb: 15000}}
 */
function parseSdResult(raw: Record<string, unknown>): SdStatus {
  let totalMb = 0;
  let freeMb = 0;

  // 1) 嘗試從 message 字串解析韌體格式 "sd_total_mb=X sd_free_mb=Y"
  const msg = typeof raw.message === 'string' ? raw.message : '';
  const totalMatch = msg.match(/sd_total_mb=(\d+)/);
  const freeMatch = msg.match(/sd_free_mb=(\d+)/);
  if (totalMatch && freeMatch) {
    totalMb = parseInt(totalMatch[1], 10);
    freeMb = parseInt(freeMatch[1], 10);
  }

  // 2) 模擬器格式：{sd: {total_mb, free_mb}}
  const sd = raw.sd as Record<string, number> | undefined;
  if (sd && typeof sd.total_mb === 'number') {
    totalMb = sd.total_mb;
    freeMb = sd.free_mb ?? 0;
  }

  // 3) 已經是 bytes 格式（未來擴充）
  if (typeof raw.total_bytes === 'number') {
    return raw as SdStatus;
  }

  if (totalMb > 0) {
    const MB = 1024 * 1024;
    return {
      mounted: true,
      total_bytes: totalMb * MB,
      free_bytes: freeMb * MB,
      used_bytes: (totalMb - freeMb) * MB,
    };
  }

  // 無法解析，返回空
  return { mounted: raw.mounted as boolean | undefined };
}

function SdStatusCard({ data }: { data: SdStatus }) {
  const total = data.total_bytes ?? 0;
  const used = data.used_bytes ?? 0;
  const free = data.free_bytes ?? (total - used);
  const pct = total > 0 ? Math.round((used / total) * 100) : 0;
  const mounted = data.mounted !== false; // 預設 true

  return (
    <div className="rounded-md border border-border bg-secondary/50 p-4 space-y-3">
      <div className="flex items-center gap-2 text-[13px] font-semibold">
        <HardDrive className="h-4 w-4 text-muted-foreground" />
        <span>SD 記憶卡狀態</span>
        <Badge variant={mounted ? 'success' : 'danger'} className="ml-auto">
          {mounted ? '已掛載' : '未掛載'}
        </Badge>
      </div>

      {total > 0 && (
        <>
          {/* 進度條 */}
          <div className="h-2.5 w-full overflow-hidden rounded-full bg-secondary">
            <div
              className="h-full rounded-full transition-all duration-500"
              style={{
                width: `${pct}%`,
                backgroundColor: pct > 90 ? '#B91C1C' : pct > 75 ? '#D97706' : '#166534',
              }}
            />
          </div>

          {/* 數字 */}
          <div className="grid grid-cols-3 gap-2 text-center text-[12px]">
            <div>
              <div className="text-muted-foreground">總容量</div>
              <div className="font-mono font-semibold">{fmtBytes(total)}</div>
            </div>
            <div>
              <div className="text-muted-foreground">已使用</div>
              <div className="font-mono font-semibold">{fmtBytes(used)}</div>
            </div>
            <div>
              <div className="text-muted-foreground">剩餘</div>
              <div className="font-mono font-semibold">{fmtBytes(free)}</div>
            </div>
          </div>
          <div className="text-center text-[11px] text-muted-foreground">
            已使用 {pct}%
          </div>
        </>
      )}
    </div>
  );
}

/* ── 確認對話框（內嵌，不用 modal） ────────────── */

function InlineConfirm({
  message,
  onConfirm,
  onCancel,
  isPending,
}: {
  message: string;
  onConfirm: () => void;
  onCancel: () => void;
  isPending: boolean;
}) {
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-md border border-destructive/30 bg-accent p-3 text-[13px]">
      <AlertTriangle className="h-4 w-4 shrink-0 text-destructive" />
      <span className="font-semibold text-accent-foreground">{message}</span>
      <div className="ml-auto flex gap-2">
        <Button size="sm" variant="secondary" onClick={onCancel} disabled={isPending}>
          取消
        </Button>
        <Button size="sm" variant="destructive" onClick={onConfirm} disabled={isPending}>
          {isPending ? '送出中…' : '確認執行'}
        </Button>
      </div>
    </div>
  );
}

/* ── 命令結果渲染 ─────────────────────────────── */

function CommandResult({ cmd }: { cmd: DeviceCommand }) {
  const result = cmd.result;
  if (!result || Object.keys(result).length === 0) return null;

  // SD 卡狀態的特殊呈現
  if (cmd.cmd_type === 'get_sd_status' && cmd.state === 'succeeded') {
    return <SdStatusCard data={parseSdResult(result as Record<string, unknown>)} />;
  }

  // 通用 JSON 呈現
  return (
    <pre className="mt-1 max-h-32 overflow-auto whitespace-pre-wrap rounded bg-secondary/60
                    px-2.5 py-1.5 font-mono text-[11px] text-muted-foreground">
      {JSON.stringify(result, null, 2)}
    </pre>
  );
}

/* ── 主元件 ──────────────────────────────────────── */

export function CommandPanel({ device }: { device: Device }) {
  const qc = useQueryClient();

  // 拉取最新命令紀錄（輪詢 5 秒），僅在面板打開時生效
  const { data: detail } = useQuery({
    queryKey: ['device-commands', device.device_id],
    queryFn: () => apiGet<DeviceDetailResp>(
      `/api/v1/devices/${encodeURIComponent(device.device_id)}`),
    refetchInterval: 5_000,
  });
  const commands = detail?.commands ?? [];

  // 最新一筆 SD 狀態結果
  const latestSd = commands.find(
    (c) => c.cmd_type === 'get_sd_status' && c.state === 'succeeded' && c.result,
  );

  // 狀態
  const [uploadFilename, setUploadFilename] = useState('');
  const [thresholdValue, setThresholdValue] = useState('');
  const [confirmReboot, setConfirmReboot] = useState(false);
  const [feedback, setFeedback] = useState<{ ok: boolean; text: string } | null>(null);
  const [historyExpanded, setHistoryExpanded] = useState(true);
  const feedbackTimer = useRef<ReturnType<typeof setTimeout>>();

  const caps = device.capabilities ?? {};

  /** 某個命令在裝置上是否可用（依能力閘門） */
  const canUse = useCallback((cmdType: string) => {
    const need = ALLOWED_COMMANDS[cmdType];
    if (need === undefined) return false;  // 不在白名單
    if (need === null) return true;         // 不受閘門
    return !!caps[need];
  }, [caps]);

  /** 送出命令的 mutation */
  const mut = useMutation({
    mutationFn: (body: { cmd_type: string; params: Record<string, unknown>; ttl_sec: number }) =>
      apiPost<{ command_id: string }>(
        `/api/v1/devices/${encodeURIComponent(device.device_id)}/commands`,
        body,
      ),
    onSuccess: (r) => {
      showFeedback(true, `已排入下發佇列（${r.command_id.slice(0, 8)}）`);
      // 2 秒後重新整理命令紀錄（讓使用者看到 sent/succeeded 狀態）
      setTimeout(() => {
        void qc.invalidateQueries({ queryKey: ['device-commands', device.device_id] });
      }, 2000);
      // 也立刻刷一次
      void qc.invalidateQueries({ queryKey: ['device-commands', device.device_id] });
    },
    onError: (e) => {
      showFeedback(false, e instanceof Error ? e.message : '下發失敗');
    },
  });

  function showFeedback(ok: boolean, text: string) {
    setFeedback({ ok, text });
    if (feedbackTimer.current) clearTimeout(feedbackTimer.current);
    feedbackTimer.current = setTimeout(() => setFeedback(null), 8000);
  }

  function sendCmd(cmdType: string, params: Record<string, unknown> = {}, ttl = 120) {
    setFeedback(null);
    setConfirmReboot(false);
    mut.mutate({ cmd_type: cmdType, params, ttl_sec: ttl });
  }

  return (
    <div className="space-y-6">
      {/* ── 快速命令按鈕 ──────────────────────────── */}
      <section>
        <h3 className="mb-3 font-mono text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
          快速命令
        </h3>
        <div className="flex flex-wrap gap-2">
          <Button
            size="sm" variant="secondary"
            disabled={!canUse('get_sd_status') || mut.isPending}
            onClick={() => sendCmd('get_sd_status')}
          >
            <HardDrive className="h-3.5 w-3.5" />
            <span>查詢記憶卡</span>
          </Button>
          <Button
            size="sm" variant="secondary"
            disabled={!canUse('request_log') || mut.isPending}
            onClick={() => sendCmd('request_log')}
          >
            <FileText className="h-3.5 w-3.5" />
            <span>索取日誌</span>
          </Button>
          <Button
            size="sm" variant="dangerSoft"
            disabled={!canUse('reboot') || mut.isPending}
            onClick={() => setConfirmReboot(true)}
          >
            <RotateCcw className="h-3.5 w-3.5" />
            <span>重新啟動</span>
          </Button>
        </div>
        {!canUse('reboot') && (
          <p className="mt-1.5 text-[11px] text-muted-foreground">
            重新啟動需要韌體支援 firmware_ota 能力
          </p>
        )}
      </section>

      {/* ── 重新啟動確認 ──────────────────────────── */}
      {confirmReboot && (
        <InlineConfirm
          message="確定要重新啟動此裝置？裝置將暫時離線。"
          isPending={mut.isPending}
          onConfirm={() => sendCmd('reboot', {}, 60)}
          onCancel={() => setConfirmReboot(false)}
        />
      )}

      {/* ── 補傳未上傳檔案（sync_missing）────────── */}
      <section>
        <h3 className="mb-2 font-mono text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
          SD 補傳
        </h3>
        <Button
          size="sm" variant="secondary"
          disabled={!canUse('request_upload') || mut.isPending}
          onClick={() => sendCmd('request_upload', {}, 300)}
        >
          <Upload className="h-3.5 w-3.5" />
          <span>補傳未上傳檔案</span>
        </Button>
        <p className="mt-1.5 text-[11px] text-muted-foreground">
          掃描 SD 卡錄影檔，自動補傳平台尚未完整收到的所有檔案
        </p>
        {/* 顯示最新 sync_missing 進度 */}
        {(() => {
          const latestSync = commands.find(
            (c) => c.cmd_type === 'request_upload' && c.result &&
              (c.result as Record<string, unknown>)?.mode === 'sync_missing' ||
              (c.result as Record<string, unknown>)?.stage !== undefined
          );
          if (!latestSync?.result) return null;
          const r = latestSync.result as Record<string, unknown>;
          const stage = r.stage as string | undefined;
          const detail = r as Record<string, unknown>;
          const isRunning = latestSync.state === 'running' || latestSync.state === 'sent';
          return (
            <div className="mt-2 rounded-md border border-border bg-secondary/50 p-3 text-[12px] space-y-1">
              <div className="flex items-center gap-2">
                {isRunning && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                {latestSync.state === 'succeeded' && <CheckCircle2 className="h-3.5 w-3.5 text-success" />}
                {latestSync.state === 'failed' && <XCircle className="h-3.5 w-3.5 text-destructive" />}
                <span className="font-semibold">
                  {stage === 'scanning' ? '掃描中…' :
                   stage === 'uploading' ? '傳輸中…' :
                   stage === 'complete' ? '補傳完成' :
                   stage === 'check_stored' ? '查詢失敗' :
                   String(detail.message || '處理中')}
                </span>
              </div>
              {(detail.scanned !== undefined) && (
                <div className="grid grid-cols-5 gap-1 text-center text-[11px]">
                  <div><div className="text-muted-foreground">掃描</div><div className="font-mono font-semibold">{Number(detail.scanned)}</div></div>
                  <div><div className="text-muted-foreground">略過</div><div className="font-mono font-semibold">{Number(detail.skipped)}</div></div>
                  <div><div className="text-muted-foreground">待傳</div><div className="font-mono font-semibold">{Number(detail.queued)}</div></div>
                  <div><div className="text-muted-foreground">完成</div><div className="font-mono font-semibold">{Number(detail.completed)}</div></div>
                  <div><div className="text-muted-foreground">失敗</div><div className="font-mono font-semibold">{Number(detail.failed)}</div></div>
                </div>
              )}
              {!!detail.in_progress && (
                <div className="text-[11px] text-muted-foreground truncate">
                  傳輸中：{String(detail.in_progress)}
                </div>
              )}
            </div>
          );
        })()}
      </section>

      {/* ── 要求上傳單一檔案（進階）─────────────── */}
      <section>
        <h3 className="mb-2 font-mono text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
          指定檔案上傳
        </h3>
        <div className="flex gap-2">
          <Input
            placeholder="輸入檔案名稱，例如 20260915_143000.avi"
            value={uploadFilename}
            onChange={(e) => setUploadFilename(e.target.value)}
            className="flex-1"
          />
          <Button
            size="sm" variant="secondary"
            disabled={!canUse('request_upload') || !uploadFilename.trim() || mut.isPending}
            onClick={() => {
              sendCmd('request_upload', { filename: uploadFilename.trim() });
              setUploadFilename('');
            }}
          >
            <Upload className="h-3.5 w-3.5" />
            <span>上傳</span>
          </Button>
        </div>
      </section>

      {/* ── 設定偵測門檻 ─────────────────────────── */}
      <section>
        <h3 className="mb-2 font-mono text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
          設定偵測門檻
        </h3>
        <div className="flex gap-2">
          <Input
            type="number"
            placeholder="門檻值（0.0–1.0）"
            value={thresholdValue}
            onChange={(e) => setThresholdValue(e.target.value)}
            className="w-40"
            min={0}
            max={1}
            step={0.05}
          />
          <Button
            size="sm" variant="secondary"
            disabled={!canUse('set_threshold') || !thresholdValue.trim() || mut.isPending}
            onClick={() => {
              sendCmd('set_threshold', { threshold: parseFloat(thresholdValue) });
              setThresholdValue('');
            }}
          >
            <SlidersHorizontal className="h-3.5 w-3.5" />
            <span>套用</span>
          </Button>
        </div>
      </section>

      {/* ── 回饋訊息 ─────────────────────────────── */}
      {feedback && (
        <div className={`flex items-center gap-2 rounded-md px-3 py-2.5 text-[13px] font-semibold ${
          feedback.ok
            ? 'bg-success-soft text-success-on'
            : 'bg-accent text-accent-foreground'
        }`}>
          {feedback.ok
            ? <CheckCircle2 className="h-4 w-4 shrink-0" />
            : <XCircle className="h-4 w-4 shrink-0" />}
          {feedback.text}
        </div>
      )}

      {/* ── 最新 SD 卡狀態 ───────────────────────── */}
      {latestSd?.result && (
        <SdStatusCard data={parseSdResult(latestSd.result as Record<string, unknown>)} />
      )}

      {/* ── 命令歷史 ─────────────────────────────── */}
      <section>
        <button
          type="button"
          className="mb-2 flex w-full items-center gap-1.5 font-mono text-[11px] font-bold
                     uppercase tracking-wider text-muted-foreground hover:text-foreground"
          onClick={() => setHistoryExpanded((v) => !v)}
        >
          {historyExpanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
          命令歷史（{commands.length}）
        </button>

        {historyExpanded && (
          commands.length > 0 ? (
            <div className="space-y-2">
              {commands.map((c) => (
                <div
                  key={c.command_id}
                  className="rounded-md border border-border bg-card p-3 text-[12.5px]"
                >
                  <div className="flex flex-wrap items-center gap-2">
                    {CMD_ICON[c.state] ?? <Clock className="h-3.5 w-3.5" />}
                    <span className="font-semibold">
                      {CMD_TYPE_LABEL[c.cmd_type] ?? c.cmd_type}
                    </span>
                    <Badge variant={CMD_VARIANT[c.state] ?? 'muted'}>
                      {CMD_LABEL[c.state] ?? c.state}
                    </Badge>
                    <span className="ml-auto font-mono text-[11px] text-muted-foreground">
                      {fmtTime(c.created_at)}
                    </span>
                  </div>
                  <CommandResult cmd={c} />
                </div>
              ))}
            </div>
          ) : (
            <p className="text-[12.5px] text-muted-foreground">尚無命令紀錄</p>
          )
        )}
      </section>
    </div>
  );
}
